import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  DollarSign, Calculator, Building2, CreditCard, Plus, Pencil, Trash2,
  TrendingDown, Home, Landmark, Wallet, ChevronRight, Phone, Mail, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  heloc: { label: 'HELOC', icon: Home, color: 'bg-emerald-100 text-emerald-700' },
  home_equity: { label: 'Home Equity Loan', icon: Landmark, color: 'bg-sky-100 text-sky-700' },
  cash_out_refi: { label: 'Cash-Out Refi', icon: Building2, color: 'bg-violet-100 text-violet-700' },
  unsecured: { label: 'Unsecured Loan', icon: CreditCard, color: 'bg-amber-100 text-amber-700' },
  cash: { label: 'Cash', icon: Wallet, color: 'bg-slate-100 text-slate-600' },
};

const LENDER_CATS = {
  local_mortgage: { label: 'Local Mortgage', color: 'bg-sky-100 text-sky-700' },
  pool_loan_partner: { label: 'Pool Loan Partner', color: 'bg-amber-100 text-amber-700' },
  credit_union: { label: 'Credit Union', color: 'bg-emerald-100 text-emerald-700' },
};

const EMPTY_PROG = { name: '', type: 'unsecured', lender_name: '', rate_type: 'fixed', rate_min: 0, rate_max: 0, display_rate: 8.0, term_months: 120, max_amount: 200000, dealer_fee_pct: 0, requires_equity: false, description: '', best_for: '', is_active: true, rank: 10 };
const EMPTY_LENDER = { name: '', category: 'local_mortgage', contact_name: '', phone: '', email: '', website: '', description: '', is_active: true, order: 10 };

function PaymentCard({ opt }) {
  const meta = TYPE_META[opt.type] || TYPE_META.unsecured;
  const Icon = meta.icon;
  return (
    <Card className="border-slate-100 hover:shadow-md transition-shadow" data-testid={`payment-opt-${opt.program_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${meta.color.split(' ')[0]} flex items-center justify-center`}>
              <Icon className={`w-4 h-4 ${meta.color.split(' ')[1]}`} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">{opt.program_name}</p>
              {opt.lender_name && <p className="text-[10px] text-slate-400">{opt.lender_name}</p>}
            </div>
          </div>
          <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
        </div>
        <div className="text-center py-4 bg-slate-50 rounded-xl mb-3">
          <p className="text-3xl font-extrabold text-slate-900 font-mono tracking-tight">
            ${Math.round(opt.monthly_payment).toLocaleString()}
          </p>
          <p className="text-xs text-slate-500 font-medium">/month</p>
        </div>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Rate</span>
            <span className="font-mono font-bold text-slate-800">{opt.rate}% <span className="text-slate-400 font-normal">{opt.rate_type}</span></span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Term</span>
            <span className="font-mono font-bold text-slate-800">{opt.term_years} years</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Total Interest</span>
            <span className="font-mono text-slate-600">${Math.round(opt.total_interest).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">Total Paid</span>
            <span className="font-mono text-slate-600">${Math.round(opt.total_paid).toLocaleString()}</span>
          </div>
          {opt.dealer_fee_pct > 0 && (
            <div className="flex justify-between items-center pt-1 border-t border-slate-100">
              <span className="text-red-500 font-medium">Dealer Fee ({opt.dealer_fee_pct}%)</span>
              <span className="font-mono text-red-600 font-bold">-${Math.round(opt.dealer_fee_amount).toLocaleString()}</span>
            </div>
          )}
          {opt.dealer_fee_pct > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Net to You</span>
              <span className="font-mono font-bold text-emerald-700">${Math.round(opt.net_to_builder).toLocaleString()}</span>
            </div>
          )}
        </div>
        {opt.requires_equity && <p className="text-[10px] text-amber-600 mt-3 font-semibold bg-amber-50 px-2 py-1 rounded">Requires home equity</p>}
      </CardContent>
    </Card>
  );
}

export default function Finance() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('calculator');
  const [programs, setPrograms] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [calcAmount, setCalcAmount] = useState(110000);
  const [calcResults, setCalcResults] = useState(null);
  const [progDialog, setProgDialog] = useState(false);
  const [editProg, setEditProg] = useState(null);
  const [progForm, setProgForm] = useState(EMPTY_PROG);
  const [lenderDialog, setLenderDialog] = useState(false);
  const [editLender, setEditLender] = useState(null);
  const [lenderForm, setLenderForm] = useState(EMPTY_LENDER);

  const load = useCallback(() => {
    api.get('/finance/programs').then(r => setPrograms(r.data)).catch(() => {});
    api.get('/finance/lenders').then(r => setLenders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runCalc = async () => {
    const r = await api.post('/finance/calculate', { amount: calcAmount });
    setCalcResults(r.data);
  };
  useEffect(() => { if (calcAmount > 0) runCalc(); }, [calcAmount]);

  const saveProg = async () => {
    const payload = { ...progForm, rate_min: parseFloat(progForm.rate_min), rate_max: parseFloat(progForm.rate_max), display_rate: parseFloat(progForm.display_rate), term_months: parseInt(progForm.term_months), max_amount: parseFloat(progForm.max_amount), dealer_fee_pct: parseFloat(progForm.dealer_fee_pct), rank: parseInt(progForm.rank) };
    if (editProg) { await api.put(`/finance/programs/${editProg.id}`, payload); toast.success('Program updated'); }
    else { await api.post('/finance/programs', payload); toast.success('Program created'); }
    setProgDialog(false); setEditProg(null); setProgForm(EMPTY_PROG); load(); runCalc();
  };

  const saveLender = async () => {
    if (editLender) { await api.put(`/finance/lenders/${editLender.id}`, lenderForm); toast.success('Lender updated'); }
    else { await api.post('/finance/lenders', lenderForm); toast.success('Lender added'); }
    setLenderDialog(false); setEditLender(null); setLenderForm(EMPTY_LENDER); load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="finance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Finance Options</h1>
          <p className="text-slate-500 text-sm mt-1">Payment calculator, programs & lender partners</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="calculator" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-calculator">
            <Calculator className="w-3.5 h-3.5" /> Payment Calculator
          </TabsTrigger>
          <TabsTrigger value="programs" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-programs">
            <CreditCard className="w-3.5 h-3.5" /> Programs ({programs.length})
          </TabsTrigger>
          <TabsTrigger value="lenders" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-lenders">
            <Building2 className="w-3.5 h-3.5" /> Lender Partners ({lenders.length})
          </TabsTrigger>
        </TabsList>

        {/* ─── CALCULATOR TAB ─── */}
        <TabsContent value="calculator">
          <Card className="border-sky-200 bg-sky-50/30 mb-6">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="flex-1 w-full sm:w-auto">
                  <Label className="text-sky-800 font-bold">Pool Project Amount</Label>
                  <div className="relative mt-1">
                    <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sky-500" />
                    <Input
                      type="number"
                      value={calcAmount}
                      onChange={e => setCalcAmount(parseFloat(e.target.value) || 0)}
                      className="pl-9 h-12 text-xl font-bold font-mono bg-white"
                      data-testid="calc-amount-input"
                    />
                  </div>
                </div>
                {calcResults && (
                  <div className="p-4 bg-white rounded-xl border-2 border-sky-300 text-center min-w-[200px] shadow-sm">
                    <p className="text-[10px] text-sky-600 font-bold uppercase tracking-wider">As Low As</p>
                    <p className="text-3xl font-extrabold text-sky-700 font-mono tracking-tight">${Math.round(calcResults.lowest_monthly).toLocaleString()}</p>
                    <p className="text-xs text-sky-500 font-medium">/month</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {calcResults && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {calcResults.options.filter(o => o.type !== 'cash').map(opt => (
                  <PaymentCard key={opt.program_id} opt={opt} />
                ))}
              </div>
              <p className="text-[10px] text-slate-400 italic">{calcResults.disclaimer}</p>
            </>
          )}
        </TabsContent>

        {/* ─── PROGRAMS TAB ─── */}
        <TabsContent value="programs">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">{programs.length} finance programs</p>
            {isAdmin && (
              <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => { setEditProg(null); setProgForm(EMPTY_PROG); setProgDialog(true); }}
                data-testid="add-program-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Program</Button>
            )}
          </div>
          <div className="space-y-3">
            {programs.map(p => {
              const meta = TYPE_META[p.type] || TYPE_META.unsecured;
              return (
                <Card key={p.id} className={`border-slate-100 ${!p.is_active ? 'opacity-50' : ''}`} data-testid={`prog-row-${p.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${meta.color.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                      <meta.icon className={`w-5 h-5 ${meta.color.split(' ')[1]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-slate-800">{p.name}</p>
                        <Badge className={`text-[9px] ${meta.color}`}>{meta.label}</Badge>
                        {!p.is_active && <Badge className="bg-red-100 text-red-600 text-[9px]">Inactive</Badge>}
                      </div>
                      <p className="text-xs text-slate-500">{p.lender_name || 'Various'} | {p.display_rate}% {p.rate_type} | {p.term_months / 12}yr</p>
                    </div>
                    <div className="text-right">
                      {p.dealer_fee_pct > 0 && <p className="text-xs text-red-500 font-mono">Dealer Fee: {p.dealer_fee_pct}%</p>}
                      <p className="text-xs text-slate-400">Rank: {p.rank}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditProg(p); setProgForm(p); setProgDialog(true); }}><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => { await api.delete(`/finance/programs/${p.id}`); toast.success('Deleted'); load(); }}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ─── LENDERS TAB ─── */}
        <TabsContent value="lenders">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">{lenders.length} lending partners</p>
            {isAdmin && (
              <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => { setEditLender(null); setLenderForm(EMPTY_LENDER); setLenderDialog(true); }}
                data-testid="add-lender-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Lender</Button>
            )}
          </div>
          {['local_mortgage', 'pool_loan_partner', 'credit_union'].map(cat => {
            const catLenders = lenders.filter(l => l.category === cat);
            if (!catLenders.length) return null;
            const catMeta = LENDER_CATS[cat];
            return (
              <div key={cat} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={catMeta.color}>{catMeta.label}</Badge>
                  <span className="text-xs text-slate-400">{catLenders.length} partners</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catLenders.map(l => (
                    <Card key={l.id} className="border-slate-100 hover:shadow-sm transition-shadow" data-testid={`lender-card-${l.id}`}>
                      <CardContent className="p-3.5">
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-sm font-bold text-slate-800">{l.name}</p>
                          {isAdmin && (
                            <div className="flex gap-0.5">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditLender(l); setLenderForm(l); setLenderDialog(true); }}><Pencil className="w-3 h-3 text-slate-400" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { await api.delete(`/finance/lenders/${l.id}`); toast.success('Removed'); load(); }}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mb-2">{l.description}</p>
                        {(l.phone || l.email || l.website) && (
                          <div className="space-y-0.5">
                            {l.phone && <p className="text-[10px] text-slate-400 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{l.phone}</p>}
                            {l.email && <p className="text-[10px] text-slate-400 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{l.email}</p>}
                            {l.website && <p className="text-[10px] text-sky-500 flex items-center gap-1"><Globe className="w-2.5 h-2.5" />{l.website}</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* ─── PROGRAM DIALOG ─── */}
      <Dialog open={progDialog} onOpenChange={setProgDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="program-dialog">
          <DialogHeader><DialogTitle>{editProg ? 'Edit Program' : 'Add Finance Program'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Program Name</Label><Input value={progForm.name} onChange={e => setProgForm(p => ({...p, name: e.target.value}))} data-testid="prog-name" /></div>
              <div><Label>Type</Label>
                <Select value={progForm.type} onValueChange={v => setProgForm(p => ({...p, type: v}))}>
                  <SelectTrigger data-testid="prog-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lender Name</Label><Input value={progForm.lender_name} onChange={e => setProgForm(p => ({...p, lender_name: e.target.value}))} data-testid="prog-lender" /></div>
              <div><Label>Rate Type</Label>
                <Select value={progForm.rate_type} onValueChange={v => setProgForm(p => ({...p, rate_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="variable">Variable</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Rate Min %</Label><Input type="number" step="0.25" value={progForm.rate_min} onChange={e => setProgForm(p => ({...p, rate_min: e.target.value}))} /></div>
              <div><Label>Rate Max %</Label><Input type="number" step="0.25" value={progForm.rate_max} onChange={e => setProgForm(p => ({...p, rate_max: e.target.value}))} /></div>
              <div><Label>Display Rate %</Label><Input type="number" step="0.25" value={progForm.display_rate} onChange={e => setProgForm(p => ({...p, display_rate: e.target.value}))} data-testid="prog-rate" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Term (months)</Label><Input type="number" value={progForm.term_months} onChange={e => setProgForm(p => ({...p, term_months: e.target.value}))} data-testid="prog-term" /></div>
              <div><Label>Max Amount</Label><Input type="number" value={progForm.max_amount} onChange={e => setProgForm(p => ({...p, max_amount: e.target.value}))} /></div>
              <div><Label>Rank</Label><Input type="number" value={progForm.rank} onChange={e => setProgForm(p => ({...p, rank: e.target.value}))} /></div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <Label className="text-red-700 font-semibold">Dealer Fee %</Label>
              <Input type="number" step="0.5" value={progForm.dealer_fee_pct} onChange={e => setProgForm(p => ({...p, dealer_fee_pct: e.target.value}))} className="mt-1" data-testid="prog-dealer-fee" />
              <p className="text-[10px] text-red-600 mt-1">Fee you pay to the lender. Impacts your net revenue and margin.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={progForm.requires_equity} onCheckedChange={v => setProgForm(p => ({...p, requires_equity: v}))} />
              <Label>Requires Home Equity</Label>
            </div>
            <div><Label>Description</Label><Textarea value={progForm.description} onChange={e => setProgForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
            <div><Label>Best For</Label><Input value={progForm.best_for} onChange={e => setProgForm(p => ({...p, best_for: e.target.value}))} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={progForm.is_active} onCheckedChange={v => setProgForm(p => ({...p, is_active: v}))} data-testid="prog-active" />
              <Label>Active (shown in calculator & proposals)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgDialog(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={saveProg} disabled={!progForm.name} data-testid="prog-save-btn">{editProg ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── LENDER DIALOG ─── */}
      <Dialog open={lenderDialog} onOpenChange={setLenderDialog}>
        <DialogContent className="max-w-md" data-testid="lender-dialog">
          <DialogHeader><DialogTitle>{editLender ? 'Edit Lender' : 'Add Lending Partner'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Lender Name</Label><Input value={lenderForm.name} onChange={e => setLenderForm(p => ({...p, name: e.target.value}))} data-testid="lender-name" /></div>
            <div><Label>Category</Label>
              <Select value={lenderForm.category} onValueChange={v => setLenderForm(p => ({...p, category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LENDER_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Contact Name</Label><Input value={lenderForm.contact_name} onChange={e => setLenderForm(p => ({...p, contact_name: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={lenderForm.phone} onChange={e => setLenderForm(p => ({...p, phone: e.target.value}))} /></div>
              <div><Label>Email</Label><Input value={lenderForm.email} onChange={e => setLenderForm(p => ({...p, email: e.target.value}))} /></div>
            </div>
            <div><Label>Website</Label><Input value={lenderForm.website} onChange={e => setLenderForm(p => ({...p, website: e.target.value}))} /></div>
            <div><Label>Description</Label><Textarea value={lenderForm.description} onChange={e => setLenderForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLenderDialog(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={saveLender} disabled={!lenderForm.name} data-testid="lender-save-btn">{editLender ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
